import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import Layout from "~/Layout";
import { getName } from "country-list";
import ReactCountryFlag from "react-country-flag";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import "../directors.scss";
import ProfileSidebar from "~/components/profile-sidebar";
import apiService from "~/services/service";
import API_ROUTES from "~/api-routes";
import API_ROUTES_V2 from "~/api-routes-v2";
import {
  DirectorFormData,
  DirectorInformationRequestBody,
  MappedAppointments,
  OtherEntityAppointment,
} from "../types/director";
import IdentificationForm from "../onboarding/components/IdentificationForm";
import AddressForm from "../onboarding/components/AddressForm";
import DirectorInformationForm from "../onboarding/components/DirectorInformationForm";
import useDirectors from "../useDirectors";
import ToastMessage from "~/components/ToastMessage";
import { Toast } from "primereact/toast";
import DirectorAppointmentHistory from "../onboarding/components/appointment-history";
import AddDirectorAppointmentModal from "../modals/AddDirectorAppointmentModal";
import DeleteModal from "~/components/modals/delete/DeleteModal";
import icon from "../../../assets/trash-icon.svg";
import DirectorProfileAttachments from "../onboarding/components/attachment";
import DeleteDirectorModal from "../modals/DeleteDirectorModal";
import { UserRole } from "~/types/roles";
import { useSelector } from "react-redux";
import TenantAuditSidebar from "~/components/audit-sidebar/TenantAuditSidebar";
import { useDirectorAudit } from "~/api/directors/hooks/useDirectorAudit";
import { useDirectorAddressAudit } from "~/api/directors/hooks/useDirectorAddressAudit";

interface DirectorData {
  id: string;
  name: string;
  other_name?: string;
  last_name: string;
  preferred_name?: string;
  email: string;
  phone: string;
  phone_code: string;
  positions?: Array<{
    position: string;
    status: string;
  }>;
  entityData?: {
    id: string;
    name: string;
    country: string;
    isEntityPlus: boolean;
    tenant_id?: string;
  };
  address?: string;
  addressData?: {
    id: string;
    country: string;
    address_line_1: string;
    address_line_2: string;
    state: string;
    city: string;
    postalcode: string;
  };
  social_security_number?: string;
  passport_number?: string;
  country_of_issue?: string;
  date_of_birth?: string;
  nationality?: string;
  expiry_date?: string;
}

const DirectorEditLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { role } = useSelector((state) => state.login);
  const { directorId, entityId } = useParams();
  const [directorData, setDirectorData] = useState<DirectorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSavingInformation, setIsSavingInformation] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingIdentification, setIsSavingIdentification] = useState(false);
  const [isEditingIdentification, setIsEditingIdentification] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isAppointmentLoading, setIsAppointmentLoading] = useState(false);
  const {
    updateDirectorInformation,
    fetchEntityDetails,
    appointments,
    setShowAddAppointmentModal,
    setIsEditAppointment,
    setEditAppointmentEntry,
    fetchDirectorAppointmentHistory,
    showAddAppointmentModal,
    setAppointments,
    editAppointmentEntry,
    isEditAppointment,
    addDirectorAppointmentHistory,
    updateDirectorAppointmentHistory,
    deleteDirectorAppointmentHistory,
    showDeleteModal,
    setShowDeleteModal,
    otherEntitiesAppointments,
    setOtherEntitiesAppointments,
    fetchOtherEntitiesAppointments,
    showDeleteDirectorModal,
    setShowDeleteDirectorModal,
    deleteDirector,
    isAuditSidebarVisible,
    setIsAuditSidebarVisible,
  } = useDirectors();
  const toastRef = useRef<Toast>(null);
  const [otherEntitiesLoading, setOtherEntitiesLoading] = useState(false);
  const [showOtherEntityDeleteModal, setShowOtherEntityDeleteModal] =
    useState(false);
  const [selectedOtherEntityAppointment, setSelectedOtherEntityAppointment] =
    useState<OtherEntityAppointment | null>(null);
  const [otherEntityDeleteMessage, setOtherEntityDeleteMessage] = useState("");
  const [thisEntityDeleteMessage, setThisEntityDeleteMessage] = useState("");
  const [isAddingForOtherEntity, setIsAddingForOtherEntity] = useState(false);
  const [isAuditSidebarOpen, setIsAuditSidebarOpen] = useState(false);
  const [
    isIdentificationAuditSidebarOpen,
    setIsIdentificationAuditSidebarOpen,
  ] = useState(false);
  const [isAddressAuditSidebarOpen, setIsAddressAuditSidebarOpen] =
    useState(false);

  // Check if user is GXM Admin or Super Admin
  const isGXMAdmin =
    role === UserRole.GXM_ADMIN || role === UserRole.SUPER_ADMIN;

  const methods = useForm<DirectorFormData>({
    mode: "onChange",
    defaultValues: {
      given_name: "",
      other_name: "",
      surname: "",
      preferred_name: "",
      email: "",
      phone_code: "",
      phone_number: "",
      social_security_number: "",
      passport_number: "",
      country_of_issue: null,
      nationality: null,
      date_of_birth: null,
      expiry_date: null,
      address_country: null,
      address_line_1: "",
      address_line_2: "",
      address_state: null,
      address_city: null,
      address_postal_code: "",
    },
  });

  const fetchDirectorData = async () => {
    try {
      setLoading(true);
      const response = await apiService(API_ROUTES_V2.FETCH_DIRECTOR, "GET", {
        replace: { id: directorId, entity_id: entityId || "" },
      });
      const data = response.data;

      // Fetch entity details using entityId from route params
      let entityData = null;
      if (entityId) {
        const entityResponse = await fetchEntityDetails(entityId);
        const hasEntityPlusAccess = entityResponse?.data?.active_services?.some(
          (e: { name: string }) => e.name === "Entities +",
        );

        if (entityResponse?.success && entityResponse?.data) {
          entityData = {
            id: entityResponse.data.id,
            name: entityResponse.data.name,
            country: entityResponse.data.country,
            isEntityPlus: hasEntityPlusAccess,
            tenant_id: entityResponse.data.tenant_id,
          };
        }
      }

      // Add entityData to director data
      const directorWithEntity = {
        ...data,
        entityData,
      };
      setDirectorData(directorWithEntity);

      // Populate form with fetched data
      methods.reset({
        given_name: data.name || "",
        other_name: data.other_name || "",
        surname: data.last_name || "",
        preferred_name: data.preferred_name || "",
        email: data.email || "",
        phone_code: data.phone_code || "",
        phone_number: data.phone || "",
        social_security_number: data.social_security_number || "",
        passport_number: data.passport_id || "",
        country_of_issue: data.country_of_issue
          ? {
              code: data.country_of_issue,
              name: getName(data.country_of_issue) || data.country_of_issue,
            }
          : null,
        nationality: data.nationality
          ? {
              code: data.nationality,
              name: getName(data.nationality) || data.nationality,
            }
          : null,
        date_of_birth: data.date_of_birth ? new Date(data.date_of_birth) : null,
        expiry_date: data.expiry_date ? new Date(data.expiry_date) : null,
        address_country: data.addressData?.country
          ? {
              code: data.addressData.country,
              name:
                getName(data.addressData.country) || data.addressData.country,
            }
          : null,
        address_line_1: data.addressData?.address_line_1 || "",
        address_line_2: data.addressData?.address_line_2 || "",
        address_state: data.addressData?.state
          ? { code: data.addressData.state, name: data.addressData.state }
          : null,
        address_city: data.addressData?.city
          ? { code: data.addressData.city, name: data.addressData.city }
          : null,
        address_postal_code: data.addressData?.postalcode || "",
      });
    } catch (error) {
      console.error("Error fetching director data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (directorId) {
      fetchDirectorData();
      fetchAppointmentHistory();
    }
  }, [directorId]);

  const fetchAppointmentHistory = async () => {
    try {
      setIsAppointmentLoading(true);
      if (!directorId) return;
      await fetchDirectorAppointmentHistory(directorId, entityId);
    } catch (error) {
      console.error(error);
    } finally {
      setIsAppointmentLoading(false);
    }
  };

  // Fetch other entities appointments - triggered when "Other Entities" tab is clicked
  const handleFetchOtherEntitiesAppointments = async () => {
    if (!directorId || !directorData?.entityData?.id) {
      console.warn(
        "Cannot fetch other entities appointments: missing directorId or entityId",
      );
      return;
    }
    try {
      setOtherEntitiesLoading(true);
      await fetchOtherEntitiesAppointments(
        directorId,
        directorData.entityData.id,
      );
    } catch (error) {
      console.error("Error fetching other entities appointments:", error);
    } finally {
      setOtherEntitiesLoading(false);
    }
  };

  // Handler to open delete modal for other entity appointment
  const handleDeleteOtherEntityAppointment = (
    appointment: OtherEntityAppointment,
  ) => {
    setSelectedOtherEntityAppointment(appointment);
    setShowOtherEntityDeleteModal(true);
  };

  // Handler to confirm deletion of other entity appointment
  const confirmDeleteOtherEntityAppointment = async () => {
    if (!directorId || !selectedOtherEntityAppointment?.id) return;
    const targetEntityId =
      selectedOtherEntityAppointment?.entity_id ||
      selectedOtherEntityAppointment?.entityData?.id;
    const entityName =
      selectedOtherEntityAppointment?.entityData?.name?.trim() ||
      "Unknown Entity";
    const wasLastForThatEntity =
      !!targetEntityId &&
      otherEntitiesAppointments.filter(
        (appt) => (appt.entity_id || appt.entityData?.id) === targetEntityId,
      ).length === 1;

    try {
      setOtherEntitiesLoading(true);
      const response: any = await deleteDirectorAppointmentHistory(
        directorId,
        selectedOtherEntityAppointment.id,
      );
      if (response?.success) {
        const removedFromEntity = response?.data?.removed_from_entity === true;
        setOtherEntitiesAppointments((prev) =>
          prev.filter((appt) => appt.id !== selectedOtherEntityAppointment.id),
        );
        const shouldRedirectAndShowRemovedToast =
          entityId && (removedFromEntity || wasLastForThatEntity);
        if (shouldRedirectAndShowRemovedToast) {
          navigate(`/entity/${entityId}/appointments-ownership?tab=directors`, {
            state: {
              hasDeleted: true,
              deletedProfile: {
                type: "Director",
                first_name: directorData?.name,
                last_name: directorData?.last_name,
              },
              directorRemovedFromEntity: true,
              entityName: entityName.trim() ? entityName : "Unknown",
            },
          });
        } else if (toastRef.current) {
          toastRef.current?.show({
            severity: "success",
            summary: "Appointment deleted",
            detail: `Appointment for ${entityName} has been removed from this profile`,
            icon: "pi pi-check",
            life: 3000,
          });
        }
      }
      setShowOtherEntityDeleteModal(false);
      setSelectedOtherEntityAppointment(null);
    } catch (error) {
      console.error("Error deleting other entity appointment:", error);
    } finally {
      setOtherEntitiesLoading(false);
    }
  };

  // Handler to open edit modal for other entity appointment
  const handleEditOtherEntityAppointment = (
    appointment: OtherEntityAppointment,
  ) => {
    // Convert OtherEntityAppointment to MappedAppointments format for the modal
    // Include entity_id so the modal knows which entity this appointment belongs to
    const mappedAppointment: MappedAppointments = {
      id: appointment.id,
      position: appointment.position,
      role: appointment.role || "",
      title: appointment.title || "",
      start_date: appointment.start_date,
      end_date: appointment.end_date,
      status: appointment.status,
      entity_id: appointment.entity_id,
    };
    setSelectedOtherEntityAppointment(appointment);
    setEditAppointmentEntry(mappedAppointment);
    setIsEditAppointment(true);
    setShowAddAppointmentModal(true);
  };

  // Reset editing states and discard unsaved changes when navigating to a different section
  useEffect(() => {
    setIsEditing(false);
    setIsEditingIdentification(false);
    setIsEditingAddress(false);
    methods.reset();
  }, [location.pathname]);

  const handleSaveIdentification = async () => {
    if (!directorId) return;

    const formData = methods.getValues();
    setIsSavingIdentification(true);

    // Build request body with only non-empty values
    const requestBody: Record<string, unknown> = {};

    if (formData.social_security_number) {
      requestBody.social_security_number = formData.social_security_number;
    }
    if (formData.passport_number) {
      requestBody.passport_id = formData.passport_number;
    }
    if (formData.country_of_issue?.code) {
      requestBody.country_of_issue = formData.country_of_issue.code;
    }
    if (formData.nationality?.code) {
      requestBody.nationality = formData.nationality.code;
    }
    if (formData.date_of_birth) {
      requestBody.date_of_birth = formData.date_of_birth;
    }
    if (formData.expiry_date) {
      requestBody.expiry_date = formData.expiry_date;
    }

    try {
      await apiService(API_ROUTES.UPDATE_DIRECTOR, "PUT", {
        replace: { id: directorId },
        body: JSON.stringify(requestBody),
      });
      // Update director state without refetching
      setDirectorData((prevData) =>
        prevData
          ? {
              ...prevData,
              social_security_number:
                formData.social_security_number ||
                prevData.social_security_number,
              passport_number:
                formData.passport_number || prevData.passport_number,
              country_of_issue:
                formData.country_of_issue?.code || prevData.country_of_issue,
              nationality: formData.nationality?.code || prevData.nationality,
              date_of_birth:
                formData.date_of_birth instanceof Date
                  ? formData.date_of_birth.toISOString()
                  : formData.date_of_birth || prevData.date_of_birth,
              expiry_date:
                formData.expiry_date instanceof Date
                  ? formData.expiry_date.toISOString()
                  : formData.expiry_date || prevData.expiry_date,
            }
          : prevData,
      );
      // Reset form with current values to update default values for discard
      methods.reset(methods.getValues());
      if (toastRef.current) {
        toastRef.current?.show({
          severity: "success",
          summary: "Identification",
          detail: "has been updated.",
          icon: "pi pi-check",
        });
      }
    } catch (error) {
      console.error("Error saving identification:", error);
    } finally {
      setIsSavingIdentification(false);
      setIsEditingIdentification(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!directorId) return;

    const formData = methods.getValues();
    setIsSavingAddress(true);

    // Build director_address object (same structure as onboarding)
    // Use ?? for optional fields to allow clearing values (send empty strings)
    const requestBody: Record<string, unknown> = {
      director_address: {
        type: "Registered",
        country: formData.address_country?.code || "",
        address_line_1: formData.address_line_1 || "",
        address_line_2: formData.address_line_2 ?? "",
        state: formData.address_state?.code ?? "",
        city: formData.address_city?.code ?? "",
        postalcode: formData.address_postal_code || "",
      },
    };

    try {
      const response = await apiService(API_ROUTES.UPDATE_DIRECTOR, "PUT", {
        replace: { id: directorId },
        body: JSON.stringify(requestBody),
      });

      // Extract the addressId from the API response
      // The API should return the address_id in the response when creating/updating address
      const newAddressId =
        (response as any)?.address_id ||
        (response as any)?.data?.address_id ||
        directorData?.addressData?.id ||
        "";

      // Update local director data
      setDirectorData((prevData) =>
        prevData
          ? {
              ...prevData,
              addressData: {
                id: newAddressId,
                country: formData.address_country?.code || "",
                address_line_1: formData.address_line_1 || "",
                address_line_2: formData.address_line_2 || "",
                state: formData.address_state?.code || "",
                city: formData.address_city?.code || "",
                postalcode: formData.address_postal_code || "",
              },
            }
          : prevData,
      );

      // If we don't have a valid addressId (new address creation), refetch to get the actual ID
      if (!newAddressId || newAddressId === "") {
        await fetchDirectorData();
      }

      // Reset form with current values to update default values for discard
      methods.reset(methods.getValues());
      if (toastRef.current) {
        toastRef.current?.show({
          severity: "success",
          summary: "Address",
          detail: "has been updated.",
          icon: "pi pi-check",
        });
      }
    } catch (error) {
      console.error("Error saving address:", error);
    } finally {
      setIsSavingAddress(false);
      setIsEditingAddress(false);
    }
  };

  const handleSaveInformation = async () => {
    if (!directorId) return;
    const formData = methods.getValues();
    setIsSavingInformation(true);
    // Build request body with only non-empty values
    const requestBody: Partial<DirectorInformationRequestBody> = {};

    if (formData.given_name) {
      requestBody.name = formData.given_name;
    }
    if (formData.surname) {
      requestBody.last_name = formData.surname;
    }
    if (formData.email) {
      requestBody.email = formData.email;
    }
    if (formData.phone_code) {
      requestBody.phone_code = formData.phone_code;
    }
    if (formData.phone_number) {
      requestBody.phone = formData.phone_number;
    }
    requestBody.preferred_name = formData.preferred_name;
    requestBody.other_name = formData.other_name;
    try {
      const response = await updateDirectorInformation(directorId, requestBody);
      if (response.success) {
        // update director state without refetching
        setDirectorData((prevData) =>
          prevData
            ? {
                ...prevData,
                name: formData.given_name || prevData.name,
                other_name: formData.other_name || prevData.other_name,
                last_name: formData.surname || prevData.last_name,
                preferred_name:
                  formData.preferred_name || prevData.preferred_name,
                email: formData.email || prevData.email,
                phone_code: formData.phone_code || prevData.phone_code,
                phone: formData.phone_number || prevData.phone,
              }
            : prevData,
        );
        methods.reset(methods.getValues());
        if (toastRef.current) {
          toastRef.current?.show({
            severity: "success",
            summary: "Director Information",
            detail: "has been updated.",
            icon: "pi pi-check",
          });
        }
      }
    } catch (error) {
      console.error("Error saving Director Information: ", error);
    } finally {
      setIsSavingInformation(false);
      setIsEditing(false);
    }
  };

  const deleteAppointmentHandler = async () => {
    try {
      setIsAppointmentLoading(true);
      if (!directorId || !editAppointmentEntry?.id) return;
      const response = await deleteDirectorAppointmentHistory(
        directorId,
        editAppointmentEntry?.id,
      );
      if (response?.success) {
        const filtered = (appointments || []).filter(
          (appt) => appt.id !== editAppointmentEntry?.id,
        );
        const remainingForThisEntity = (
          entityId ? filtered.filter((appt) => appt.entity_id === entityId) : []
        ).length;

        setAppointments((prevAppointments: MappedAppointments[]) => {
          const next = prevAppointments.filter(
            (appt) => appt.id !== editAppointmentEntry?.id,
          );
          return next.sort(
            (a, b) =>
              new Date(b.start_date || "").getTime() -
              new Date(a.start_date || "").getTime(),
          );
        });
        // Show inline delete notification (same as Other Entities)
        const entityName = directorData?.entityData?.name || "Unknown Entity";
        const position = editAppointmentEntry?.position || "Unknown";
        setThisEntityDeleteMessage(
          `Appointment for ${entityName} and position ${position} removed. The appointment was deleted from this profile.`,
        );
        setTimeout(() => {
          setThisEntityDeleteMessage("");
        }, 5000);

        if (remainingForThisEntity === 0 && entityId) {
          const entityName =
            directorData?.entityData?.name?.trim() || "Unknown Entity";
          navigate(`/entity/${entityId}/appointments-ownership?tab=directors`, {
            state: {
              hasDeleted: true,
              deletedProfile: {
                type: "Director",
                first_name: directorData?.name,
                last_name: directorData?.last_name,
              },
              directorRemovedFromEntity: true,
              entityName:
                entityName && entityName.trim() ? entityName : "Unknown Entity",
            },
          });
        }
      }
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting appointment:", error);
    } finally {
      setIsAppointmentLoading(false);
    }
  };

  const handleAddedAppointmentForOtherEntity = async (
    targetEntityId: string | { id?: string },
  ) => {
    if (!directorId || !directorData) return;
    let targetEntityIdValue: string | undefined;
    if (typeof targetEntityId === "string") {
      targetEntityIdValue = targetEntityId;
    } else if (
      targetEntityId &&
      typeof targetEntityId === "object" &&
      targetEntityId.id
    ) {
      targetEntityIdValue = targetEntityId.id;
    }

    if (!targetEntityIdValue) return;

    const currentId = directorData?.entityData?.id;
    const rawEntities = directorData?.entities || [];

    const existingIds: string[] = rawEntities
      .map((e: any) => {
        if (typeof e === "string") {
          return e;
        }
        if (e && typeof e === "object" && e.id) {
          return e.id;
        }
        return null;
      })
      .filter((id: string | null): id is string => Boolean(id));

    const normalizedEntityIds: string[] = [
      ...existingIds,
      currentId,
      targetEntityIdValue,
    ]
      .map((id: any) => {
        if (typeof id === "string") return id;
        if (id && typeof id === "object" && id.id) return id.id;
        return null;
      })
      .filter((id: string | null): id is string => Boolean(id));

    const entities = Array.from(new Set(normalizedEntityIds));

    if (entities.length > (existingIds.length || 0)) {
      await updateDirectorInformation(directorId, { entities });
    }
  };

  const sections = [
    {
      path: "info",
      label: "Director Information",
      content: () => (
        <DirectorInformationForm
          isEditMode={true}
          isSaving={isSavingInformation}
          onSave={handleSaveInformation}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          setIsAuditSidebarVisible={setIsAuditSidebarVisible}
          isAuditSidebarVisible={isAuditSidebarVisible}
          directorId={directorId}
        />
      ),
    },
    {
      path: "appointment-history",
      label: "Appointment History",
      content: () => (
        <DirectorAppointmentHistory
          showModal={(value, forOtherEntity) => {
            setShowAddAppointmentModal(value);
            setIsAddingForOtherEntity(!!forOtherEntity);
          }}
          showDeleteModal={setShowDeleteModal}
          appointments={appointments}
          setEditAppointmentEntry={setEditAppointmentEntry}
          setIsEditAppointment={setIsEditAppointment}
          loading={isAppointmentLoading}
          isEditMode={true}
          otherEntitiesAppointments={otherEntitiesAppointments}
          otherEntitiesLoading={otherEntitiesLoading}
          onFetchOtherEntitiesAppointments={
            handleFetchOtherEntitiesAppointments
          }
          onEditOtherEntityAppointment={handleEditOtherEntityAppointment}
          onDeleteOtherEntityAppointment={handleDeleteOtherEntityAppointment}
          currentEntityId={directorData?.entityData?.id}
          otherEntityDeleteMessage={otherEntityDeleteMessage}
          thisEntityDeleteMessage={thisEntityDeleteMessage}
          directorId={directorId}
          entityId={entityId}
        />
      ),
    },
    {
      path: "identification",
      label: "Identification",
      content: () => (
        <IdentificationForm
          isEditMode
          onSave={handleSaveIdentification}
          isSaving={isSavingIdentification}
          isEditing={isEditingIdentification}
          setIsEditing={setIsEditingIdentification}
          onViewHistory={() => setIsIdentificationAuditSidebarOpen(true)}
          showViewHistory={isGXMAdmin}
        />
      ),
    },
    {
      path: "address",
      label: "Address",
      content: () => (
        <AddressForm
          isEditMode
          onSave={handleSaveAddress}
          isSaving={isSavingAddress}
          isEditing={isEditingAddress}
          setIsEditing={setIsEditingAddress}
          onViewHistory={() => setIsAddressAuditSidebarOpen(true)}
          showViewHistory={isGXMAdmin}
        />
      ),
    },
    ...(directorData?.entityData?.isEntityPlus
      ? [
          {
            path: "attachments",
            label: "Attachments",
            content: () => (
              <DirectorProfileAttachments directorData={directorData} />
            ),
          },
        ]
      : []),
  ];

  const tabs = sections.map((section) => ({
    label: section.label,
    link: `/entity/${entityId}/directors/${directorId}/${section.path}`,
    code: section.path,
  }));

  const getActiveSection = () => {
    const currentPath = location.pathname;
    const activeSection = sections.find((section) =>
      currentPath.includes(`/${section.path}`),
    );
    return activeSection?.path || "info";
  };

  const renderContent = () => {
    const activePath = getActiveSection();
    const activeSection = sections.find((s) => s.path === activePath);
    return activeSection?.content() || sections[0].content();
  };

  const breadcrumbs = [
    {
      label: <span className="breadcrumb-link">My Entities</span>,
      command: () => navigate("/entities"),
      id: "entities",
    },
    {
      label: (
        <div className="flex">
          {!loading && directorData?.entityData ? (
            <span className="breadcrumb-link">
              {directorData.entityData.name} (
              <ReactCountryFlag
                countryCode={directorData.entityData.country}
                svg
              />{" "}
              {getName(directorData.entityData.country)})
            </span>
          ) : (
            <>
              <Skeleton width="100px" height="20px" className="mr-1" />
              <Skeleton width="150px" height="20px" className="mr-1" />
            </>
          )}
        </div>
      ),
      command: () =>
        directorData?.entityData &&
        navigate(`/entity/${directorData.entityData.id}/dashboard`),
      id: "entity",
    },
    {
      label: <span className="breadcrumb-link">Appointments & Ownership</span>,
      command: () =>
        directorData?.entityData &&
        navigate(
          `/entity/${directorData.entityData.id}/appointments-ownership`,
        ),
      id: "directors",
    },
    {
      label: (
        <>
          {!loading && directorData ? (
            directorData.preferred_name ||
            `${directorData.name} ${directorData.last_name}`
          ) : (
            <Skeleton width="100px" height="20px" className="mr-1" />
          )}
        </>
      ),
      id: "director",
    },
  ];

  const deleteDirectorbtnClickHandler = () => {
    setShowDeleteDirectorModal(true);
  };

  const closeDeleteDirectorModalHandler = () => {
    setShowDeleteDirectorModal(false);
  };

  const deleteDirectorHandler = async () => {
    try {
      setIsSavingInformation(true);
      if (!directorId) return;
      const response = await deleteDirector(directorId);
      if (response?.success) {
        closeDeleteDirectorModalHandler();
        navigate(`/entity/${entityId}/appointments-ownership?tab=directors`, {
          state: {
            hasDeleted: true,
            deletedProfile: {
              type: "Director",
              first_name: directorData?.name,
              last_name: directorData?.last_name,
            },
          },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSavingInformation(false);
    }
  };

  return (
    <Layout crumbs={breadcrumbs}>
      <FormProvider {...methods}>
        <div className="grid director-edit-container">
          <div className="col-3">
            <ProfileSidebar
              profileDetails={{
                given_name: directorData?.name || "",
                surname: directorData?.last_name || "",
                other_name: directorData?.other_name,
                preferred_name: directorData?.preferred_name,
                country: directorData?.entityData?.country || "",
                email: directorData?.email || "",
                phone_code: directorData?.phone_code,
                phone_number: directorData?.phone,
              }}
              title={
                // Filter to current entity and get first active position
                appointments
                  .filter(
                    (p) => p.entity_id === entityId && p.status === "Active",
                  )
                  .map((p) => p.position)[0] || "Director"
              }
              isLoading={loading}
              activeTab={getActiveSection()}
              tabs={tabs}
              entityLink={
                directorData?.entityData
                  ? {
                      name: directorData.entityData.name,
                      url: `/entity/${directorData.entityData.id}/dashboard`,
                    }
                  : undefined
              }
              isActive={appointments.some(
                (p) => p.entity_id === entityId && p.status === "Active",
              )}
              hasDeleteBtn={[UserRole.GXM_ADMIN].includes(role)}
              deleteBtnHanlder={deleteDirectorbtnClickHandler}
              showContactInfo={
                ![UserRole.LSP_MANAGER, UserRole.LSP_USER].includes(role)
              }
            />
          </div>
          <div className="col-9">
            <Card className="card-onboard">{renderContent()}</Card>
          </div>
        </div>
      </FormProvider>
      <AddDirectorAppointmentModal
        visible={showAddAppointmentModal}
        onHideModal={() => {
          if (
            (selectedOtherEntityAppointment || isAddingForOtherEntity) &&
            directorData?.entityData?.id &&
            directorId
          ) {
            fetchOtherEntitiesAppointments(
              directorId,
              directorData.entityData.id,
            );
          }
          setIsEditAppointment(false);
          setEditAppointmentEntry(null);
          setSelectedOtherEntityAppointment(null);
          setShowAddAppointmentModal(false);
          setIsAddingForOtherEntity(false);
        }}
        setAppointments={setAppointments}
        editAppointmentEntry={editAppointmentEntry}
        isEditAppointment={isEditAppointment}
        appointments={appointments}
        isEditMode={true}
        directorId={directorId}
        entityId={directorData?.entityData?.id}
        addDirectorAppointmentHistory={addDirectorAppointmentHistory}
        updateDirectorAppointmentHistory={updateDirectorAppointmentHistory}
        isLoading={isAppointmentLoading}
        setIsLoading={setIsAppointmentLoading}
        toastRef={toastRef}
        otherEntitiesAppointments={otherEntitiesAppointments}
        currentEntityId={directorData?.entityData?.id}
        currentTenantId={directorData?.entityData?.tenant_id}
        isAddingForOtherEntity={isAddingForOtherEntity}
        onAddedAppointmentForOtherEntity={
          directorId && directorData
            ? handleAddedAppointmentForOtherEntity
            : undefined
        }
      />

      <DeleteModal
        visible={showDeleteModal}
        closeModal={() => setShowDeleteModal(false)}
        header={`Do you really want to remove ${directorData?.name} ${directorData?.last_name} position of ${editAppointmentEntry?.position}?`}
        description="Deleting will remove the appointment for this director in the selected entity and all related items from the user interface, including Position, Role, Dates and Status.“"
        deleteHandler={deleteAppointmentHandler}
        isDeleteLoading={isAppointmentLoading}
        icon={
          <img className="users-card-icon-img" src={icon} alt="Users Icon" />
        }
      />

      {/* Delete Modal for Other Entities appointments */}
      <DeleteModal
        visible={showOtherEntityDeleteModal}
        closeModal={() => {
          setShowOtherEntityDeleteModal(false);
          setSelectedOtherEntityAppointment(null);
        }}
        header="Are you sure you want to delete this Director appointment?"
        description="Deleting will remove the appointment for this director in the selected entity and all related items from the user interface, including Position, Role, Dates and Status."
        deleteHandler={confirmDeleteOtherEntityAppointment}
        isDeleteLoading={otherEntitiesLoading}
        icon={
          <img className="users-card-icon-img" src={icon} alt="Users Icon" />
        }
      />

      <DeleteDirectorModal
        visible={showDeleteDirectorModal}
        closeModal={closeDeleteDirectorModalHandler}
        deleteHandler={deleteDirectorHandler}
        isDeleteLoading={isSavingInformation}
      />

      {isGXMAdmin && isAuditSidebarOpen && entityId && directorId && (
        <TenantAuditSidebar
          heading="Director Information"
          sidebarToggle={isAuditSidebarOpen}
          setSidebarToggle={setIsAuditSidebarOpen}
          tenantId={entityId}
          labels={() => ({
            create: "Director Profile Created",
            update: `Information Updated\n${directorData?.preferred_name || `${directorData?.name || ""} ${directorData?.last_name || ""}`.trim()}`,
            delete: "Director Profile Deleted",
          })}
          useAuditHook={(_entityId: string, enabled: boolean) =>
            useDirectorAudit(directorId, enabled)
          }
        />
      )}
      {isGXMAdmin &&
        isIdentificationAuditSidebarOpen &&
        entityId &&
        directorId && (
          <TenantAuditSidebar
            heading="Identification"
            sidebarToggle={isIdentificationAuditSidebarOpen}
            setSidebarToggle={setIsIdentificationAuditSidebarOpen}
            tenantId={entityId}
            labels={() => ({
              create: "Identification Created",
              update: "Identification Updated",
              delete: "Identification Deleted",
            })}
            useAuditHook={(_tenantId: string, enabled: boolean) =>
              useDirectorAudit(directorId, enabled, "identification")
            }
          />
        )}

      {isGXMAdmin && isAddressAuditSidebarOpen && entityId && directorId && (
        <TenantAuditSidebar
          heading="Address"
          sidebarToggle={isAddressAuditSidebarOpen}
          setSidebarToggle={setIsAddressAuditSidebarOpen}
          tenantId={directorData?.addressData?.id || entityId}
          labels={() => ({
            create: "Address Added",
            update: "Address Updated",
            delete: "Address Deleted",
          })}
          useAuditHook={(_tenantId: string, enabled: boolean) =>
            useDirectorAddressAudit(
              directorId,
              directorData?.addressData?.id || "",
              enabled,
            )
          }
        />
      )}

      <ToastMessage ref={toastRef} />
    </Layout>
  );
};

export default DirectorEditLayout;
